/**
 * V70.28: 조직관리 > 인력 현황 — Executive Workforce Dashboard
 *
 * 주 사용자: 이사/경영진. 상세 인사관리 화면이 아니라 **인력 규모와 변화를 한 화면에서** 보는 용도.
 * 위에서 아래로 다음 질문에 순서대로 답한다.
 *   ① 지금 몇 명인가  ② 어떤 고용형태로 구성되어 있는가  ③ 지난 1년간 어떻게 변했는가
 *   ④ 입·퇴사가 어떻게 발생하는가  ⑤ 앞으로 변경/만료될 인력은 누구인가
 *   ⑥ 올해 어떤 전환이 있었는가  ⑦ 각 조직의 현재 구성은 어떠한가
 *
 * 데이터: `getWorkforceOverviewReport` callable 1개 (조직 범위/권한은 전부 서버 결정)
 * 차트: `components/workforceOverview/charts.tsx` (CSS+SVG 자체 구현, 신규 의존성 없음)
 * 색상: `components/workforceOverview/palette.ts` 단일 정의
 *
 * 접근 권한: `WorkforceOverviewAccessGuard` (라우트) + Sidebar 노출 필터 + 서버 재검증
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, BarChart3, RefreshCw, Users, CalendarClock, ArrowRightLeft, Building2,
  ChevronLeft, ChevronRight, Loader2,
} from 'lucide-react';
import { useWorkforceOverviewAccess } from '../../shared/hooks/useWorkforceOverviewAccess';
import { useWorkforceOverviewReport } from '../../shared/hooks/useWorkforceOverviewReport';
import {
  EmploymentLineChart, DonutChart, JoinLeaveChart, JoinLeaveLegend,
  EmploymentTypeLegend, ActualProjectedLegend,
  ContractEndChart, ContractEndLegend,
} from './components/workforceOverview/charts';
import {
  EMPLOYMENT_TYPE_ORDER, typeStyle,
  CONTRACT_END_ORDER, contractEndStyle,
} from './components/workforceOverview/palette';
import type { EmploymentTypeKey } from './components/workforceOverview/palette';
import { sortOrgRowsByName } from './components/workforceOverview/orgSorting';

// ============================================================
// 공통 UI 조각
// ============================================================
const cardStyle: React.CSSProperties = {
  background: 'white',
  border: '1px solid var(--color-gray-200)',
  borderRadius: 8,
  padding: 20,
};

function SectionCard({ title, icon, right, subtitle, children }: {
  title: string;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ ...cardStyle, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: subtitle ? 2 : 14, flexWrap: 'wrap' }}>
        {icon}
        <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: 'var(--color-gray-900)' }}>{title}</h2>
        <div style={{ marginLeft: 'auto' }}>{right}</div>
      </div>
      {subtitle && (
        <p style={{ fontSize: 11.5, color: 'var(--color-gray-500)', margin: '0 0 14px' }}>{subtitle}</p>
      )}
      {children}
    </section>
  );
}

function EmptyBox({ text }: { text: string }) {
  return (
    <div style={{
      padding: '28px 16px', textAlign: 'center', fontSize: 13, color: 'var(--color-gray-500)',
      border: '1px dashed var(--color-gray-300)', borderRadius: 6,
    }}>
      {text}
    </div>
  );
}

const dateInputStyle: React.CSSProperties = {
  padding: '4px 8px', fontSize: 12.5, border: '1px solid var(--color-gray-300)',
  borderRadius: 6, color: 'var(--color-gray-800)', background: 'white',
};

const thStyle: React.CSSProperties = {
  padding: '8px 10px', fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)',
  borderBottom: '1px solid var(--color-gray-200)', textAlign: 'left', whiteSpace: 'nowrap',
};
const tdStyle: React.CSSProperties = {
  padding: '8px 10px', fontSize: 13, color: 'var(--color-gray-800)',
  borderBottom: '1px solid var(--color-gray-100)', whiteSpace: 'nowrap',
};

function TypeBadge({ code, label }: { code: string; label: string }) {
  const s = typeStyle(code);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 8px', borderRadius: 10, fontSize: 12,
      background: s.soft, color: s.color, fontWeight: 600,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.color }} />
      {label}
    </span>
  );
}

function DDayBadge({ dDay }: { dDay: number }) {
  const urgent = dDay <= 30;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 10, fontSize: 11.5, fontWeight: 600,
      background: urgent ? '#fdf0ee' : 'var(--color-gray-100)',
      color: urgent ? '#a8544c' : 'var(--color-gray-600)',
    }}>
      {dDay === 0 ? 'D-DAY' : `D-${dDay}`}
    </span>
  );
}

function fmtDate(ymd: string): string {
  return ymd ? ymd.replace(/-/g, '.') : '-';
}

/** V70.35: 비율 표기 — 분모 0 이면 0.0% (NaN/Infinity 방지), 소수 1자리 */
function pctText(part: number, total: number): string {
  if (!total || !Number.isFinite(part / total)) return '0.0%';
  return `${(Math.round((part / total) * 1000) / 10).toFixed(1)}%`;
}
/**
 * `계약 만료 예정` 테이블 전용 고용형태 라벨.
 *
 *  **V70.49: 표시 기준을 `serviceYear`(고용형태별 근속연차)로 변경했다.**
 *    BE 가 `employmentHistories` 체인에서 산출한다 — 고용형태가 바뀌면 전환일부터
 *    1년차로 초기화되므로, 파견 근속이 계약직 연차에 합산되지 않는다.
 *
 *  ⚠️ `contractSequence`(V70.44, 계약 **갱신 횟수**)로 **fallback 하지 않는다.**
 *     두 값은 측정 대상이 다르다. 예: 29일 계약 + 335일 연장 →
 *     `contractSequence = 2` 이지만 통산 364일이라 **근속은 1년차**다.
 *     같은 `N년차` 라벨 아래 두 의미를 섞으면 숫자를 오독하게 된다.
 *     `contractSequence` 는 데이터·API 에 그대로 보존되며 다른 용도로 쓸 수 있다.
 *
 *  `serviceYear` 가 없으면(BE 가 확정 불가 판단) **기존 고용형태 라벨 그대로** 반환한다.
 *    · `1년차` 로 fallback 하지 않는다
 *    · `?년차` 를 쓰지 않는다
 *    · 입사일/이력 기간으로 FE 가 재계산하지 않는다
 *  3년차 이상도 하드코딩 없이 그대로 표기된다 (`파견직 3년차`) — 정책 위반 데이터를
 *  임의로 2년차로 보정하지 않는다.
 *
 *  대상은 DISPATCH / CONTRACT 뿐이다. 정규직·인턴은 연차 개념이 없으므로 원래 라벨을 쓴다.
 */
export function contractEndTypeLabel(
  row: { code: string; label: string; serviceYear?: number; contractSequence?: number }
): string {
  if (row.code !== 'DISPATCH' && row.code !== 'CONTRACT') return row.label;
  const yr = row.serviceYear;
  if (typeof yr !== 'number' || !Number.isInteger(yr) || yr < 1) return row.label;
  return `${row.label} ${yr}년차`;
}

/** 서버가 계산한 비율값을 소수 1자리로 표기 */
function rateText(v: number): string {
  return `${(Number.isFinite(v) ? v : 0).toFixed(1)}%`;
}

// ============================================================
// V70.31: 기준월 컨트롤
//   URL query `?month=YYYY-MM` 로 유지 → 새로고침/공유 시에도 기준월 보존
// ============================================================
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

function kstThisMonth(): string {
  const kst = new Date(Date.now() + 9 * 60 * 60 * 1000);
  return kst.toISOString().slice(0, 7);
}
function shiftMonth(ym: string, delta: number): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}
function fmtMonth(ym: string): string {
  const [y, m] = ym.split('-');
  return `${y}년 ${Number(m)}월`;
}

function MonthPicker({ month, onChange, isLoading }: {
  month: string; onChange: (ym: string) => void; isLoading: boolean;
}) {
  const btn: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, background: 'white', color: 'var(--color-gray-700)',
    border: '1px solid var(--color-gray-300)', borderRadius: 6, cursor: 'pointer', padding: 0,
  };
  const isThisMonth = month === kstThisMonth();
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>기준월</span>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: 4, background: 'var(--color-gray-50)',
        border: '1px solid var(--color-gray-200)', borderRadius: 8,
      }}>
        <button style={btn} onClick={() => onChange(shiftMonth(month, -1))} title="이전 달" aria-label="이전 달">
          <ChevronLeft size={15} />
        </button>
        <span style={{
          minWidth: 96, textAlign: 'center', fontSize: 13.5, fontWeight: 700,
          color: 'var(--color-gray-900)',
        }}>
          {fmtMonth(month)}
        </span>
        <button style={btn} onClick={() => onChange(shiftMonth(month, 1))} title="다음 달" aria-label="다음 달">
          <ChevronRight size={15} />
        </button>
      </div>
      {!isThisMonth && (
        <button
          onClick={() => onChange(kstThisMonth())}
          style={{
            padding: '5px 10px', fontSize: 12, background: 'white', color: 'var(--color-gray-700)',
            border: '1px solid var(--color-gray-300)', borderRadius: 6, cursor: 'pointer',
          }}
        >
          이번 달
        </button>
      )}
      {isLoading && <Loader2 size={14} className="spin" color="var(--color-gray-400)" />}
    </div>
  );
}

/** 기준일 + 과거/당월/미래 표기 */
function BasisBadge({ asOfDate, basis }: { asOfDate: string; basis: 'past' | 'current' | 'future' }) {
  const text = basis === 'current' ? '현재' : basis === 'past' ? '월말' : '예정';
  const color = basis === 'future' ? '#b08440' : 'var(--color-gray-600)';
  return (
    <span style={{ fontSize: 13, color, fontWeight: 600 }}>
      {fmtDate(asOfDate)} 기준 <span style={{ fontWeight: 400, opacity: 0.8 }}>({text})</span>
    </span>
  );
}

// ============================================================
// Page
// ============================================================
export function OrgWorkforceOverviewPage() {
  const { visibleOrgUnitCount, isAdmin, isLoading: accessLoading } = useWorkforceOverviewAccess();

  // 기준월은 URL query `?month=YYYY-MM` 로 유지 (새로고침해도 보존)
  const [searchParams, setSearchParams] = useSearchParams();
  const monthParam = searchParams.get('month') || '';
  const month = useMemo(
    () => (MONTH_RE.test(monthParam) ? monthParam : kstThisMonth()),
    [monthParam],
  );
  const setMonth = useCallback((ym: string) => {
    const next = new URLSearchParams(searchParams);
    if (ym === kstThisMonth()) next.delete('month');
    else next.set('month', ym);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // V70.36: 계약 만료 조회기간 — 기본값은 기준월 연도 1/1~12/31
  const defaultCeRange = useMemo(() => {
    const y = month.slice(0, 4);
    return { startDate: `${y}-01-01`, endDate: `${y}-12-31` };
  }, [month]);
  /** null = 기본기간(연도 따라 자동 변경) / 값 = 사용자가 직접 지정한 custom 기간 */
  const [ceCustomRange, setCeCustomRange] = useState<{ startDate: string; endDate: string } | null>(null);
  const ceCustom = ceCustomRange !== null;
  const ceRange = ceCustomRange ?? defaultCeRange;
  const setCeRange = useCallback((r: { startDate: string; endDate: string }) => {
    if (r.startDate && r.endDate && r.startDate > r.endDate) return;   // 잘못된 역순 입력 무시
    setCeCustomRange(r);
  }, []);
  const resetCeRange = useCallback(() => setCeCustomRange(null), []);

  const { report, isLoading, error, refetch } = useWorkforceOverviewReport(month, ceCustomRange);

  const noOrgConfigured = !accessLoading && visibleOrgUnitCount === 0;

  // ── Header ──
  const header = (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>인력 현황</h1>
        <MonthPicker month={month} onChange={setMonth} isLoading={isLoading} />
        {report && <BasisBadge asOfDate={report.asOfDate} basis={report.basis} />}
        <button
          onClick={refetch}
          disabled={isLoading}
          title="새로고침"
          style={{
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', fontSize: 12.5, background: 'white',
            border: '1px solid var(--color-gray-300)', borderRadius: 6,
            cursor: isLoading ? 'not-allowed' : 'pointer', color: 'var(--color-gray-700)',
          }}
        >
          <RefreshCw size={13} /> 새로고침
        </button>
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-gray-500)', margin: '4px 0 0' }}>
        선택된 조직의 인력 구성과 변화를 확인합니다.
      </p>
      {report && (
        <p style={{ fontSize: 12, color: 'var(--color-gray-400)', margin: '4px 0 0' }}>
          조회 조직 {report.scope.orgUnitCount}개 (직접 소속 직원만 집계)
        </p>
      )}
    </div>
  );

  // ── 조직 미설정 ──
  if (noOrgConfigured) {
    return (
      <div style={{ padding: 24 }}>
        {header}
        <div style={cardStyle}>
          <EmptyBox text={isAdmin
            ? 'Admin > 인력 현황 관리에서 조회할 조직을 먼저 설정해주세요.'
            : '인력 현황 관리에서 조회할 조직을 먼저 설정해주세요. (관리자에게 요청해 주세요)'} />
        </div>
      </div>
    );
  }

  // ── Loading skeleton ──
  if (isLoading && !report) {
    return (
      <div style={{ padding: 24 }}>
        {header}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} style={{ ...cardStyle, height: 92, background: 'var(--color-gray-50)' }} />
          ))}
        </div>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ ...cardStyle, height: 240, marginBottom: 20, background: 'var(--color-gray-50)' }} />
        ))}
        <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--color-gray-500)' }}>인력 현황을 불러오는 중...</p>
      </div>
    );
  }

  // ── Error ──
  if (error || !report) {
    return (
      <div style={{ padding: 24 }}>
        {header}
        <div style={{ ...cardStyle, textAlign: 'center', padding: 40 }}>
          <AlertTriangle size={36} color="var(--color-red-500)" style={{ marginBottom: 12 }} />
          <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>인력 현황을 불러오지 못했습니다.</h2>
          {error?.message && (
            <p style={{ fontSize: 12.5, color: 'var(--color-gray-500)', marginBottom: 20 }}>{error.message}</p>
          )}
          <button
            onClick={refetch}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 20px', fontSize: 13,
              background: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer',
            }}
          >
            <RefreshCw size={13} /> 다시 시도
          </button>
        </div>
      </div>
    );
  }

  const { currentSummary, monthlyTrend, monthlyJoinLeave,
    organizationBreakdown: rawOrganizationBreakdown,
    upcomingConversions, upcomingContractEnds, yearlyConversions, dataQuality, contractEnd: ce } = report;

  /*
    V70.40: 조직 행 **표시 순서만** 공통 comparator 로 통일한다.
      BE 는 두 영역을 서로 다른 기준으로 정렬해 내려준다
        · 인력 구성  = orgUnit.order → name
        · 계약 결과  = decided 내림차순 → name
      그래서 같은 조직이 두 표에서 다른 순서로 보였다.
      여기서는 집계 결과 객체를 그대로 두고 배열 순서만 바꾼다 (값·비율·컬럼 구성 불변).
  */
  const organizationBreakdown = sortOrgRowsByName(rawOrganizationBreakdown);
  const ceOrgRows = sortOrgRowsByName(ce.org.rows);

  // V70.36: 계약 만료 세부 결과 카드 구성 — 실제 데이터가 있는 유형만 노출
  // V70.37: 모든 항목이 확정 결과이므로 rate 는 항상 숫자다 ('비율 제외' 상태 없음)
  const ceBreakdown: Array<{ key: string; label: string; count: number; rate: number; color: string }> = [
    ...EMPLOYMENT_TYPE_ORDER
      .filter((c) => (ce.summary.convertedByType[c] || 0) > 0)
      .map((c) => ({
        key: `conv-${c}`,
        label: `${typeStyle(c).label} 전환`,
        count: ce.summary.convertedByType[c] || 0,
        rate: ce.summary.convertedRateByType[c] ?? 0,
        color: contractEndStyle('converted').color,
      })),
    ...EMPLOYMENT_TYPE_ORDER
      .filter((c) => (ce.summary.extendedByType[c] || 0) > 0)
      .map((c) => ({
        key: `ext-${c}`,
        label: `${typeStyle(c).label} 연장`,
        count: ce.summary.extendedByType[c] || 0,
        rate: ce.summary.extendedRateByType[c] ?? 0,
        color: contractEndStyle('extended').color,
      })),
    { key: 'ended', label: '종료', count: ce.summary.ended, rate: ce.summary.endedRate, color: contractEndStyle('ended').color },
  ];

  /**
   * V70.38: 조직별 테이블 컬럼
   *   - 아래 기본 컬럼은 조회기간 내 전체 조직에서 0건이어도 **항상 노출**한다.
   *     (정규직 전환 / 계약직 전환 / 파견직 연장 / 계약직 연장 / 종료)
   *     "0건이라서 컬럼이 사라지는" 동작은 지표가 없어진 것처럼 읽히기 때문이다.
   *   - 그 외 유형(인턴 전환, 파견직 전환, 정규직 연장, 인턴 연장 등)은
   *     기존대로 실제 결과가 1건 이상 존재할 때만 동적으로 추가한다.
   *   BE 응답은 이미 4개 코드 전부를 0 포함해 내려주므로 FE 전용 변경이다.
   */
  const FIXED_CONV: EmploymentTypeKey[] = ['REGULAR', 'CONTRACT'];
  const FIXED_EXT: EmploymentTypeKey[] = ['DISPATCH', 'CONTRACT'];

  const ceOrgColumns: Array<{
    key: string; label: string; color: string;
    count: (o: typeof ce.org.rows[number]) => number;
    rate: (o: typeof ce.org.rows[number]) => number;
  }> = [
    ...EMPLOYMENT_TYPE_ORDER
      .filter((c) => FIXED_CONV.includes(c) || ceOrgRows.some((o) => (o.convertedByType[c] || 0) > 0))
      .map((c) => ({
        key: `conv-${c}`, label: `${typeStyle(c).label} 전환`, color: contractEndStyle('converted').color,
        count: (o: typeof ce.org.rows[number]) => o.convertedByType[c] || 0,
        rate: (o: typeof ce.org.rows[number]) => o.convertedRateByType[c] ?? 0,
      })),
    ...EMPLOYMENT_TYPE_ORDER
      .filter((c) => FIXED_EXT.includes(c) || ceOrgRows.some((o) => (o.extendedByType[c] || 0) > 0))
      .map((c) => ({
        key: `ext-${c}`, label: `${typeStyle(c).label} 연장`, color: contractEndStyle('extended').color,
        count: (o: typeof ce.org.rows[number]) => o.extendedByType[c] || 0,
        rate: (o: typeof ce.org.rows[number]) => o.extendedRateByType[c] ?? 0,
      })),
    {
      key: 'ended', label: '종료', color: contractEndStyle('ended').color,
      count: (o: typeof ce.org.rows[number]) => o.ended,
      rate: (o: typeof ce.org.rows[number]) => o.endedRate,
    },
  ];

  const orderedTypes = EMPLOYMENT_TYPE_ORDER
    .map((c) => currentSummary.byType.find((t) => t.code === c))
    .filter((t): t is NonNullable<typeof t> => !!t);

  return (
    <div style={{ padding: 24 }}>
      {header}

      {/* 데이터 품질 경고 — console 에서 끝내지 않고 화면에 표시 */}
      {dataQuality.warnings.length > 0 && (
        <div style={{
          display: 'flex', gap: 10, padding: '12px 14px', marginBottom: 20, borderRadius: 6,
          background: '#fffaf0', border: '1px solid #f0d8a8',
        }}>
          <AlertTriangle size={16} color="#b08440" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 12.5, color: '#7a5a28' }}>
            <strong style={{ display: 'block', marginBottom: 4 }}>데이터 확인이 필요합니다</strong>
            {dataQuality.warnings.map((w, i) => <div key={i}>· {w}</div>)}
          </div>
        </div>
      )}

      {/* ① KPI */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 12, marginBottom: 20,
      }}>
        <div style={{ ...cardStyle, borderLeft: '3px solid var(--color-gray-700)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--color-gray-600)', marginBottom: 6 }}>
            <Users size={14} /> 총 재직인원
          </div>
          <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--color-gray-900)', lineHeight: 1.1 }}>
            {currentSummary.total}<span style={{ fontSize: 15, fontWeight: 500, marginLeft: 2 }}>명</span>
          </div>
        </div>
        {orderedTypes.map((t) => (
          <div key={t.code} style={{ ...cardStyle, borderLeft: `3px solid ${typeStyle(t.code).color}` }}>
            <div style={{ fontSize: 12.5, color: 'var(--color-gray-600)', marginBottom: 6 }}>{t.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-gray-900)', lineHeight: 1.15 }}>
              {t.count}<span style={{ fontSize: 13, fontWeight: 500, marginLeft: 2 }}>명</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: typeStyle(t.code).color, marginTop: 2 }}>
              {t.ratio.toFixed(1)}%
            </div>
          </div>
        ))}
      </div>

      {/*
        ② 올해 인력 추이 — V70.30: **한 Row 전체 폭** 사용.
        경영진이 1~12월 12개 point × 4개 line 을 한눈에 봐야 하므로
        Donut 과 같은 Row 에 두지 않고 가용 폭을 전부 쓴다.
      */}
      <SectionCard
        title={`${report.year}년 인력 추이`}
        icon={<BarChart3 size={16} color="var(--color-gray-600)" />}
        subtitle="과거 월은 월말 기준 · 이번 달은 현재 기준 · 이후 월은 등록된 예약 정보만 반영한 예정치"
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <EmploymentTypeLegend />
            <ActualProjectedLegend />
          </div>
        }
      >
        <EmploymentLineChart data={monthlyTrend} />
      </SectionCard>

      {/* ③④ 현재 고용형태 비율 + 월별 입·퇴사 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) minmax(0, 2fr)', gap: 20, marginBottom: 20 }}
        className="wfo-two-col">
        <SectionCard
          title="고용형태 비율"
          icon={<Users size={16} color="var(--color-gray-600)" />}
          subtitle={`${fmtDate(report.asOfDate)} 기준`}
        >
          <DonutChart
            slices={orderedTypes.map((t) => ({ code: t.code, label: t.label, count: t.count, ratio: t.ratio }))}
            total={currentSummary.total}
          />
        </SectionCard>

        <SectionCard
          title={`${report.year}년 월별 입·퇴사 현황`}
          icon={<ArrowRightLeft size={16} color="var(--color-gray-600)" />}
          subtitle="조회 대상 조직 구성원을 기준으로 한 추이입니다. 미래 월은 등록된 퇴사 예약만 표시합니다."
          right={<JoinLeaveLegend />}
        >
          <JoinLeaveChart data={monthlyJoinLeave} />
        </SectionCard>
      </div>

      {/* ⑤ 예정 인력 변화 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 20, marginBottom: 20 }}>
        <SectionCard
          title="전환 예정자"
          icon={<CalendarClock size={16} color="var(--color-gray-600)" />}
          subtitle="향후 3개월 이내 고용형태 변경이 예약된 인력"
        >
          {upcomingConversions.length === 0 ? (
            <EmptyBox text="현재 등록된 전환 예정자가 없습니다." />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>이름</th>
                    <th style={thStyle}>조직</th>
                    <th style={thStyle}>현재</th>
                    <th style={thStyle}>예정</th>
                    <th style={thStyle}>예정일</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingConversions.map((c, i) => (
                    <tr key={i}>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{c.name}</td>
                      <td style={tdStyle}>{c.orgUnitName}</td>
                      <td style={tdStyle}><TypeBadge code={c.fromCode} label={c.fromLabel} /></td>
                      <td style={tdStyle}><TypeBadge code={c.toCode} label={c.toLabel} /></td>
                      <td style={tdStyle}>
                        {fmtDate(c.effectiveDate)} <DDayBadge dDay={c.dDay} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        <SectionCard
          title="계약 만료 예정"
          icon={<CalendarClock size={16} color="var(--color-gray-600)" />}
          subtitle="향후 3개월 이내 계약직 · 인턴 · 파견직 종료 예정 (정규직 제외)"
        >
          {upcomingContractEnds.length === 0 ? (
            <EmptyBox text="향후 3개월 내 계약 만료 예정자가 없습니다." />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>이름</th>
                    <th style={thStyle}>조직</th>
                    <th style={thStyle}>고용형태</th>
                    <th style={thStyle}>종료 예정일</th>
                    <th style={thStyle}>D-Day</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingContractEnds.map((c, i) => (
                    <tr key={i}>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{c.name}</td>
                      <td style={tdStyle}>{c.orgUnitName}</td>
                      {/*
                        V70.45: 이 테이블에 한해 계약 차수를 덧붙인다.
                          공용 TypeBadge 는 건드리지 않고 **label 문자열만** 조합하므로
                          다른 화면의 TypeBadge 표시는 영향받지 않는다.
                      */}
                      <td style={tdStyle}><TypeBadge code={c.code} label={contractEndTypeLabel(c)} /></td>
                      <td style={tdStyle}>{fmtDate(c.endDate)}</td>
                      <td style={tdStyle}><DDayBadge dDay={c.dDay} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>

      {/*
        ⑤ V70.36 계약 만료 현황 — 사용자 지정 조회기간
           기본값 = 기준월 연도 1/1~12/31. 사용자가 기간을 바꾸면 custom 상태로 고정되고
           기준월 연도가 바뀌어도 유지된다 (reset 버튼으로 기본값 복귀).
      */}
      <SectionCard
        title="계약 만료 현황"
        icon={<CalendarClock size={16} color="var(--color-gray-600)" />}
        subtitle="계약 만료가 도래한 이벤트 기준입니다. 한 사람이 기간 내 여러 번 만료될 수 있어 단위는 '건'입니다. 비율은 결과가 확정된 건(전환·연장·종료)만으로 계산합니다."
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>조회기간</span>
            <input
              type="date"
              value={ceRange.startDate}
              max={ceRange.endDate}
              onChange={(e) => setCeRange({ ...ceRange, startDate: e.target.value })}
              style={dateInputStyle}
            />
            <span style={{ color: 'var(--color-gray-400)' }}>~</span>
            <input
              type="date"
              value={ceRange.endDate}
              min={ceRange.startDate}
              onChange={(e) => setCeRange({ ...ceRange, endDate: e.target.value })}
              style={dateInputStyle}
            />
            {ceCustom && (
              <button
                onClick={resetCeRange}
                style={{
                  padding: '5px 10px', fontSize: 12, background: 'white', color: 'var(--color-gray-700)',
                  border: '1px solid var(--color-gray-300)', borderRadius: 6, cursor: 'pointer',
                }}
              >
                기본기간
              </button>
            )}
            <ContractEndLegend />
          </div>
        }
      >
        {/* 요약 — 만료 도래 + 세부 결과 */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 12, marginBottom: 18,
        }}>
          <div style={{ ...cardStyle, padding: 14, borderLeft: '3px solid var(--color-gray-700)' }}>
            <div style={{ fontSize: 12.5, color: 'var(--color-gray-600)', marginBottom: 4 }}>만료 도래</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--color-gray-900)', lineHeight: 1.1 }}>
              {ce.summary.total}<span style={{ fontSize: 14, fontWeight: 500, marginLeft: 2 }}>건</span>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--color-gray-400)', marginTop: 2 }}>
              {fmtDate(ce.queryRange.startDate)} ~ {fmtDate(ce.queryRange.endDate)}
            </div>
          </div>
          {ceBreakdown.map((b) => (
            <div key={b.key} style={{ ...cardStyle, padding: 14, borderLeft: `3px solid ${b.color}` }}>
              <div style={{ fontSize: 12.5, color: 'var(--color-gray-600)', marginBottom: 4 }}>{b.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-gray-900)', lineHeight: 1.15 }}>
                {b.count}<span style={{ fontSize: 13, fontWeight: 500, marginLeft: 2 }}>건</span>
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: b.color, marginTop: 2 }}>
                {rateText(b.rate)}
              </div>
            </div>
          ))}
        </div>

        {/*
          V70.37: 분모를 숨기지 않고 'total 중 decided 기준' 으로 명시한다.
          확정되지 않은 잔여 건수는 별도 상태로 라벨하지 않고, 제외 사유만 사실대로 적는다.
        */}
        <div style={{ fontSize: 11.5, color: 'var(--color-gray-500)', marginBottom: 10 }}>
          만료 도래 {ce.summary.total}건 중 결과가 확정된 {ce.summary.decided}건 기준 ·
          {' '}전환 {rateText(ce.summary.convertedRate)} · 연장 {rateText(ce.summary.extendedRate)} · 종료 {rateText(ce.summary.endedRate)}
          {ce.summary.unplanned > 0
            && ` · 나머지 ${ce.summary.unplanned}건은 전환·연장·종료 예약이 등록되지 않아 비율에서 제외했습니다.`}
        </div>

        {ce.byMonth.length === 0 ? (
          <EmptyBox text="선택한 기간에 계약 만료 도래 건이 없습니다." />
        ) : (
          <ContractEndChart data={ce.byMonth} />
        )}
      </SectionCard>

      {/* ⑧ V70.36 조직별 계약 결과 — 기준월 포함 최근 12개월 고정 */}
      <SectionCard
        title="조직별 계약 결과 · 최근 12개월"
        icon={<Building2 size={16} color="var(--color-gray-600)" />}
        subtitle="각 셀은 건수와 확정 대상 대비 비율입니다. 비율 분모는 결과가 확정된 건(전환·연장·종료)입니다."
        right={
          <span style={{
            padding: '3px 9px', borderRadius: 10, fontSize: 11.5, fontWeight: 600,
            background: 'var(--color-gray-100)', color: 'var(--color-gray-600)',
          }}>
            {ce.org.startDate.slice(0, 7).replace('-', '.')} ~ {ce.org.endDate.slice(0, 7).replace('-', '.')} · 현재 조직 기준
          </span>
        }
      >
        {ceOrgRows.every((o) => o.total === 0) ? (
          <EmptyBox text="최근 12개월에 계약 만료가 도래한 건이 없습니다." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>조직</th>
                  {/* V70.37: '미결' 열 제거. 만료 도래(total) 를 앞에 노출해 분모 관계를 드러낸다 */}
                  <th style={{ ...thStyle, textAlign: 'right' }}>만료 도래</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>확정 대상</th>
                  {ceOrgColumns.map((c) => (
                    <th key={c.key} style={{ ...thStyle, textAlign: 'right' }}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ceOrgRows.map((o) => (
                  <tr key={o.orgUnitId}>
                    <td style={tdStyle}>{o.orgUnitName}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--color-gray-600)' }}>{o.total}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{o.decided}</td>
                    {ceOrgColumns.map((c) => {
                      const cnt = c.count(o);
                      const rate = c.rate(o);
                      // V70.38: 0건은 '0' 하나로만 표시한다 ('0건 / 0.0%' 2줄은 노이즈).
                      //         '-' 나 빈칸은 쓰지 않는다 — 측정된 0 과 데이터 없음을 구분해야 하기 때문.
                      if (cnt === 0) {
                        return (
                          <td key={c.key} style={{ ...tdStyle, textAlign: 'right', color: 'var(--color-gray-300)' }}>
                            0
                          </td>
                        );
                      }
                      return (
                        <td key={c.key} style={{ ...tdStyle, textAlign: 'right' }}>
                          <div style={{ fontWeight: 600, color: 'var(--color-gray-800)' }}>
                            {cnt}건
                          </div>
                          <div style={{ fontSize: 11, color: c.color }}>
                            {rateText(rate)}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* ⑥ 올해 전환 현황 */}
      <SectionCard
        title={`${yearlyConversions.year}년 고용형태 전환 이력`}
        icon={<ArrowRightLeft size={16} color="var(--color-gray-600)" />}
        right={
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-gray-800)' }}>
            올해 총 {yearlyConversions.total}건
          </span>
        }
      >
        {yearlyConversions.total === 0 ? (
          <EmptyBox text={`${yearlyConversions.year}년에 완료된 고용형태 전환이 없습니다.`} />
        ) : (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              {yearlyConversions.pairs.map((p, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 14px', borderRadius: 6,
                    background: typeStyle(p.toCode).soft,
                    border: `1px solid ${typeStyle(p.toCode).color}22`,
                  }}
                >
                  <span style={{ fontSize: 12.5, color: 'var(--color-gray-700)' }}>
                    {p.fromLabel} <span style={{ color: 'var(--color-gray-400)' }}>→</span> {p.toLabel}
                  </span>
                  <strong style={{ fontSize: 15, color: typeStyle(p.toCode).color }}>{p.count}</strong>
                </div>
              ))}
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 320, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>전환일</th>
                    <th style={thStyle}>이름</th>
                    <th style={thStyle}>조직</th>
                    <th style={thStyle}>변경 전</th>
                    <th style={thStyle}>변경 후</th>
                  </tr>
                </thead>
                <tbody>
                  {yearlyConversions.items.map((it, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>{fmtDate(it.effectiveDate)}</td>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{it.name}</td>
                      <td style={tdStyle}>{it.orgUnitName}</td>
                      <td style={tdStyle}><TypeBadge code={it.fromCode} label={it.fromLabel} /></td>
                      <td style={tdStyle}><TypeBadge code={it.toCode} label={it.toLabel} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </SectionCard>

      {/* ⑦ 조직별 인력 구성 */}
      {/*
        V70.30: 인력 현황 관리에서 **명시 선택한 조직만** row 로 표시하고 (flat, indent/tree 없음),
        각 row 의 숫자는 **그 조직에 직접 소속된 인원**이다 (하위 조직 제외).
        orgUnitId 로 분류하므로 row 간 중복이 없고 row 합계 == 전체 총원이 항상 성립한다.
      */}
      <SectionCard
        title="조직별 인력 구성"
        icon={<Building2 size={16} color="var(--color-gray-600)" />}
        subtitle={
          `${fmtDate(report.asOfDate)} 기준 · 선택한 조직에 직접 소속된 인원만 표시합니다 (하위 조직 미포함).`
          + (report.basis === 'past'
            ? ' 조직 구분은 현재 조직 소속 기준이며, 당시 조직 구성을 재현한 값이 아닙니다.'
            : '')
        }
      >
        {organizationBreakdown.length === 0 ? (
          <EmptyBox text="조회 대상 조직에 재직 인력이 없습니다." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>조직</th>
                  {/* V70.31: label 만 '전체 인원' 으로 변경. 집계는 V70.30 정책(직접 소속) 그대로 */}
                  <th style={{ ...thStyle, textAlign: 'right' }}>전체 인원</th>
                  {/* V70.35: 정규/파견/계약은 인원 + 비율, 인턴은 인원만 */}
                  {EMPLOYMENT_TYPE_ORDER.map((c) => (
                    <React.Fragment key={c}>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{typeStyle(c).label}</th>
                      {c !== 'INTERN' && (
                        <th style={{ ...thStyle, textAlign: 'right', color: 'var(--color-gray-500)' }}>
                          {typeStyle(c).label} %
                        </th>
                      )}
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {organizationBreakdown.map((o) => (
                  <tr key={o.orgUnitId}>
                    <td style={tdStyle}>{o.orgUnitName}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{o.total}</td>
                    {EMPLOYMENT_TYPE_ORDER.map((c) => {
                      const cnt = o.byType[c] || 0;
                      return (
                        <React.Fragment key={c}>
                          <td style={{ ...tdStyle, textAlign: 'right', color: cnt === 0 ? 'var(--color-gray-300)' : typeStyle(c).color, fontWeight: cnt === 0 ? 400 : 600 }}>
                            {cnt}
                          </td>
                          {c !== 'INTERN' && (
                            <td style={{ ...tdStyle, textAlign: 'right', color: cnt === 0 ? 'var(--color-gray-300)' : 'var(--color-gray-600)' }}>
                              {pctText(cnt, o.total)}
                            </td>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))}
                {/* V70.30: row 간 중복이 없으므로 합계 == 전체 총원 */}
                <tr style={{ background: 'var(--color-gray-50)' }}>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>합계</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>
                    {organizationBreakdown.reduce((s, o) => s + o.total, 0)}
                  </td>
                  {EMPLOYMENT_TYPE_ORDER.map((c) => {
                    const sum = organizationBreakdown.reduce((acc, o) => acc + (o.byType[c] || 0), 0);
                    const grand = organizationBreakdown.reduce((acc, o) => acc + o.total, 0);
                    return (
                      <React.Fragment key={c}>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{sum}</td>
                        {c !== 'INTERN' && (
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: 'var(--color-gray-600)' }}>
                            {pctText(sum, grand)}
                          </td>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* 기준 설명 */}
      <div style={{ fontSize: 11.5, color: 'var(--color-gray-400)', lineHeight: 1.7 }}>
        {dataQuality.notes.map((n, i) => <div key={i}>· {n}</div>)}
        <div>· 고용형태와 재직 여부는 고용이력(employmentHistories)을 기준으로 해당 시점 값으로 계산됩니다.</div>
      </div>

      {/* 좁은 화면에서 2단 → 1단 */}
      <style>{`
        @media (max-width: 1100px) {
          .wfo-two-col { grid-template-columns: minmax(0, 1fr) !important; }
        }
      `}</style>
    </div>
  );
}

export default OrgWorkforceOverviewPage;
